'use client';

import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
// TinyMCE React wrapper - assets loaded from CDN (media.tracverse.com)
import { Editor } from '@tinymce/tinymce-react';

// TinyMCE CDN base URL (self-hosted on our S3/CloudFront)
const TINYMCE_CDN_URL = 'https://media.tracverse.com/shared/tinymce';

import { Navbar } from '../../components/layout/navbar';
import { Footer } from '../../components/layout/footer';
import { Button } from '../../components/ui/button';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ForumCommentItem } from '../../components/forum/forum-comment-item';
import { CommentPositionTracker } from '../../components/forum/comment-position-tracker';
import { VerificationBadge } from '../../components/feedback/verification-badge';
import { ReportDialog } from '../../components/modals/report-dialog';
import { ShareButton } from '../../components/feedback/share-button';
import {
  ArrowLeft,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Flag,
  Share2,
  Snowflake,
  MoreVertical,
  Edit,
  Trash2,
  RotateCcw,
  LayoutGrid,
  List as ListIcon,
  Eye,
  Zap,
} from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { ForumBackground } from '../../components/forum/forum-background';
import { ProcessingSpinner } from '../../components/feedback/processing-spinner';
import { UniqueViewCelebration } from '../../components/feedback/unique-view-celebration';
import { useForum } from '../../hooks/use-forum';
import { useAuthStore } from '../../stores';
import { useGetUserById, useGetContentViewStatus } from '../../hooks/use-user';
import { useComments } from '../../hooks/use-comments';
import { useGetAllCategories } from '../../hooks/use-categories';
import { useCommunities } from '../../hooks/use-communities';
import { MobileCategoriesDialog } from '../../components/forum/mobile-categories-dialog';
import { DiscussionVersionTabs } from '../../components/forum/discussion-version-tabs';
import { ChevronDown } from 'lucide-react';
import React from 'react';
import axiosInstance from '../../utils/axios-config';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  extractOidFromSlug,
  getDiscussionPath,
  generateSlug,
} from '../../utils/slug';
import { TagBadgeList } from '../../components/ui/tag-badge';

// Helper to check if discussion is within restore window
const isWithinRestoreWindow = (discussion) => {
  if (!discussion?.deleted || !discussion?.deletedAt) return false;
  const deletedAt = new Date(discussion.deletedAt).getTime();
  const fiveMinutesInMs = 5 * 60 * 1000;
  return Date.now() - deletedAt < fiveMinutesInMs;
};

// Memoized DiscussionHeader to prevent unnecessary rerenders
const DiscussionHeader = React.memo(function DiscussionHeader({
  creatorProfile,
  isLoadingCreator,
  discussion,
  getCreatorInitials,
  currentUser,
  isEditing,
  setDeleteTarget,
  setDeleteDialogOpen,
  onRestore,
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4">
      <div className="flex items-center gap-3">
        {isLoadingCreator ? (
          <div className="h-10 w-10 rounded-full bg-zinc-800 animate-pulse flex-shrink-0"></div>
        ) : (
          <Avatar className="h-10 w-10 border border-zinc-700 flex-shrink-0">
            <AvatarImage
              src={creatorProfile?.avatar || '/placeholder.svg'}
              alt={creatorProfile?.username}
            />
            <AvatarFallback className="bg-zinc-800 text-zinc-400">
              {getCreatorInitials()}
            </AvatarFallback>
          </Avatar>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-medium text-white truncate">
              {creatorProfile?.username || 'Unknown User'}
            </span>
            {creatorProfile?.isVerified && (
              <VerificationBadge
                creator={discussion?.creator}
                mintInfo={discussion?.mintInformation}
                oid={discussion?.oid}
                ual={discussion?.ual}
                compact={true}
              />
            )}
          </div>
          <p className="text-xs text-zinc-400 truncate">
            Posted{' '}
            {discussion?.createdAt
              ? new Date(discussion?.createdAt).toLocaleDateString()
              : ''}
            {discussion?.currentVersion > 1 && discussion?.updatedAt && (
              <span className="text-zinc-500">
                {' • Last edited '}
                {new Date(discussion.updatedAt).toLocaleDateString()}
              </span>
            )}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {discussion ? (
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              className="border-white/10"
              style={{
                backgroundColor: discussion?.color
                  ? `${discussion.color}20`
                  : undefined,
                color: discussion?.color || 'white',
                borderColor: discussion?.color
                  ? `${discussion.color}40`
                  : undefined,
              }}
            >
              {typeof discussion?.category === 'string'
                ? discussion.category
                : discussion?.category?.name || 'Uncategorized'}
            </Badge>
            {/* Discussion Tags */}
            {discussion?.tags && discussion.tags.length > 0 && (
              <TagBadgeList
                tags={discussion.tags.map(tag =>
                  typeof tag === 'string' ? tag : tag?.name || tag?.id || String(tag)
                )}
                size="sm"
                maxVisible={5}
                showOverflow={true}
              />
            )}
          </div>
        ) : (
          <Badge variant="outline" className="border-white/10 text-zinc-400">
            Uncategorized
          </Badge>
        )}
        {/* Edit Dropdown for Creators - hide when editing */}
        {currentUser?.creator === discussion?.creator && !isEditing && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="ml-1 text-zinc-400 hover:text-white hover:bg-white/10"
              >
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-zinc-900 border-zinc-800 text-white"
            >
              <DropdownMenuItem
                className="hover:bg-zinc-800 cursor-pointer"
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    window.dispatchEvent(
                      new CustomEvent('startEditDiscussion')
                    );
                  }
                }}
              >
                <Edit className="h-4 w-4 mr-2 text-blue-400" />
                Edit Discussion
              </DropdownMenuItem>
              {isWithinRestoreWindow(discussion) ? (
                <DropdownMenuItem
                  onClick={onRestore}
                  className="text-emerald-400 hover:bg-emerald-900/20 cursor-pointer"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Restore
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={() => {
                    setDeleteTarget(discussion.oid);
                    setDeleteDialogOpen(true);
                  }}
                  className="text-red-400 hover:bg-red-900/20 cursor-pointer"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
});

export default function ForumDiscussionPage() {
  const { discussionId } = useParams();
  // Extract OID from SEO-friendly slug URL (e.g., "my-discussion-title-abc123" -> "abc123")
  const discussionOid = extractOidFromSlug(discussionId);
  const location = useLocation();
  const navigate = useNavigate();
  const [commentContent, setCommentContent] = useState('');
  const [category, setCategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categoriesCollapsed, setCategoriesCollapsed] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [spinnerStatus, setSpinnerStatus] = useState('processing');
  const [spinnerError, setSpinnerError] = useState(null);

  // Get wallet address from Passport SSO auth store (replaces useActiveAccount)
  const { walletAddress, user: storeUser, isAuthenticated } = useAuthStore();

  // Track which discussion OID has had its view recorded
  const viewRecordedOidRef = useRef(null);
  // Track if we've shown the registration toast
  const hasShownRegistrationToast = useRef(false);

  // Unique view celebration state
  const [showUniqueViewCelebration, setShowUniqueViewCelebration] =
    useState(false);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedContent, setEditedContent] = useState('');
  const [editReason, setEditReason] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [restoreData, setRestoreData] = useState(null); // { discussionOid, deadline }
  const [restoreTimeLeft, setRestoreTimeLeft] = useState(0);
  const editEditorRef = useRef(null);

  // Use forum hooks - call useForum once and destructure all needed functions
  const forumHooks = useForum();
  const {
    useGetDiscussionByOid,
    useGetCommentsByDiscussion,
    useGetAllDiscussions,
    upvoteInteraction,
    downvoteInteraction,
    deleteDiscussion,
    restoreDiscussion,
    addView,
  } = forumHooks;

  // Get stable reference to addView mutation
  const addViewMutation = addView();

  // Fetch user profile by wallet address (current user)
  const {
    data: user,
    isLoading: isLoadingUser,
    isFetched: isUserFetched,
    error: userError,
  } = useGetUserById(walletAddress);

  // Redirect unregistered or unverified users to complete registration
  useEffect(() => {
    // Only check if user is authenticated via Passport SSO
    if (!isAuthenticated) return;

    // Wait for user query to actually complete (isFetched is true after first successful/failed fetch)
    if (!isUserFetched) return;

    // Check if user doesn't exist OR exists but is not verified
    const userNotFound = userError?.response?.status === 404 || !user;
    const userNotVerified = user && !user.isVerified;

    if (userNotFound || userNotVerified) {
      // Only show toast once to prevent duplicates
      if (!hasShownToastRef.current) {
        hasShownToastRef.current = true;
        toast.error('Complete Your Profile', {
          description: userNotVerified
            ? 'Please complete your profile verification to access discussions.'
            : 'Please complete your profile registration to access discussions.',
          action: {
            label: 'Complete Profile',
            onClick: () => navigate('/profile/complete-registration'),
          },
          duration: 5000,
        });
      }
      navigate('/profile/complete-registration');
    }
  }, [isAuthenticated, user, userError, isUserFetched, navigate]);

  // Determine if we are looking up by OID (legacy) or Slug (new)
  // Simple heuristic: OID is usually short/alphanumeric with potential uppercase.
  // Slug is usually longer, lowercase, with hyphens.
  // BUT valid OID extracted might match the whole slug if no hyphens.
  // Strategy: Try to fetch by OID first IF it looks like an OID.
  // If we can't be sure, we might need a cascading lookup or parallel lookup.
  // Assuming "discussionId" param contains the full slug from URL.

  // Fetch ALL discussions to support slug lookup
  // Note: Only fetch if necessary (client side filtering)
  const { data: allDiscussions, isLoading: isAllDiscussionsLoading } =
    useGetAllDiscussions({}, { enabled: true });

  // 1. Try to find by slug matching from ALL discussions
  const discussionBySlug = allDiscussions?.find(
    (d) =>
      generateSlug(d.title) === discussionId ||
      generateSlug(d.title) === discussionId?.split('-').slice(0, -1).join('-')
  );

  // 2. Legacy/Direct OID lookup - only if discussionOid looks like a valid OID (contains uppercase/numbers and is short)
  const isValidOid = discussionOid && /^[A-Za-z0-9]{8,15}$/.test(discussionOid);
  const { data: discussionByOid, isLoading: isLoadingDiscussionByOid } =
    useGetDiscussionByOid(isValidOid ? discussionOid : null);

  // Consolidated discussion object
  const discussion = discussionBySlug || discussionByOid;
  const isLoadingDiscussion =
    (!discussion && isAllDiscussionsLoading) ||
    (!discussion && isValidOid && isLoadingDiscussionByOid);

  // Use the correct OID for subsequent hooks
  const finalDiscussionOid = discussion?.oid || discussionOid;

  // Fetch the discussion creator's profile
  const {
    data: creatorProfile,
    isLoading: isLoadingCreator,
    error: creatorError,
  } = useGetUserById(discussion?.creator);

  // Check community membership (if discussion belongs to a community)
  const { useCheckMembership } = useCommunities();
  const { data: membershipData, isLoading: isLoadingMembership } =
    useCheckMembership(discussion?.communityOid, user?.oid);
  const isMember = membershipData?.isMember || false;
  const canPost = membershipData?.canPost || false;

  // Fetch user's view status on this discussion
  const { data: viewStatus } = useGetContentViewStatus(
    discussion?.oid,
    walletAddress
  );

  const { comments, isCommentsLoading, addDiscussionComment } = useComments(
    finalDiscussionOid,
    'discussion'
  );

  // Fetch categories from API
  const {
    data: apiCategories,
    isLoading: isLoadingCategories,
    error: categoriesError,
  } = useGetAllCategories();

  // Use API categories
  const categories = apiCategories || [];

  // Fetch category data when discussion changes
  useEffect(() => {
    if (discussion?.topics && discussion?.topics.length > 0) {
      setCategory(null);
    }
  }, [discussion?.topics]);

  // Increment view count when discussion is loaded (only once per discussion)
  useEffect(() => {
    // Only record view if we haven't already recorded it for this specific OID
    if (!discussion?.oid || viewRecordedOidRef.current === discussion.oid) {
      return;
    }

    // Mark as recorded BEFORE making the API call to prevent race conditions
    viewRecordedOidRef.current = discussion.oid;

    // Gather comprehensive view information
    const referrer = document.referrer || '';
    const referrerDomain = referrer
      ? new URL(referrer, window.location.href).hostname
      : '';

    // Gather device and browser information
    const viewMetadata = {
      // Include discussion title for activity logs
      title: discussion.title,
      pageUrl: window.location.href,
      pageTitle: document.title,
      referrer,
      referrerDomain,
      tabId:
        sessionStorage.getItem('tabId') || `tab_${Date.now()}_${Math.random()}`,
      windowSize: `${window.innerWidth}x${window.innerHeight}`,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      colorDepth: window.screen.colorDepth,
      pixelRatio: window.devicePixelRatio,
      language: navigator.language,
      languages: navigator.languages ? Array.from(navigator.languages) : [],
      platform: navigator.platform,
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack === '1',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset(),
    };

    // Add connection info if available
    if (navigator.connection) {
      viewMetadata.connectionType = navigator.connection.effectiveType;
      viewMetadata.connectionDownlink = navigator.connection.downlink;
      viewMetadata.connectionRtt = navigator.connection.rtt;
    }

    // Store tab ID for session tracking
    if (!sessionStorage.getItem('tabId')) {
      sessionStorage.setItem('tabId', viewMetadata.tabId);
    }

    // Log view - ref check above ensures this only runs once per OID
    addViewMutation.mutate(
      {
        discussionOid: discussion.oid,
        userWallet: walletAddress,
        metadata: viewMetadata,
      },
      {
        onSuccess: (data) => {
          // Show celebration if this was a new unique view
          if (data?.isNewUniqueView) {
            setShowUniqueViewCelebration(true);
          }
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discussion?.oid]);

  // Listen for edit button click from dropdown
  useEffect(() => {
    const handleEditEvent = () => {
      handleStartEdit();
    };

    window.addEventListener('startEditDiscussion', handleEditEvent);
    return () => {
      window.removeEventListener('startEditDiscussion', handleEditEvent);
    };
  }, [discussion]);

  // Countdown timer for restore window
  useEffect(() => {
    if (!restoreData) return;

    const updateCountdown = () => {
      const now = Date.now();
      const deadline = new Date(restoreData.deadline).getTime();
      const remaining = Math.max(0, Math.floor((deadline - now) / 1000));

      setRestoreTimeLeft(remaining);

      if (remaining <= 0) {
        setRestoreData(null);
        toast({
          title: 'Restore window expired',
          description: 'The discussion can no longer be restored.',
          variant: 'destructive',
        });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [restoreData, toast]);

  // Check if discussion is already deleted on load and set up restore banner
  useEffect(() => {
    if (discussion?.deleted && discussion?.deletedAt) {
      const deletedAt = new Date(discussion.deletedAt).getTime();
      const fiveMinutesInMs = 5 * 60 * 1000;
      const deadline = deletedAt + fiveMinutesInMs;

      // Only show restore banner if within 5-minute window
      if (Date.now() < deadline) {
        setRestoreData({
          discussionOid: discussion.oid,
          deadline: new Date(deadline).toISOString(),
        });
      }
    }
  }, [discussion?.deleted, discussion?.deletedAt, discussion?.oid]);

  // Handle restore discussion
  const handleRestoreDiscussion = async () => {
    // Use restoreData if available, otherwise use discussion.oid directly
    const oidToRestore = restoreData?.discussionOid || discussion?.oid;
    if (!oidToRestore) return;

    try {
      await restoreDiscussion().mutateAsync({
        discussionOid: oidToRestore,
      });
      setRestoreData(null);
      toast({
        title: 'Discussion restored',
        description: 'Your discussion has been restored successfully.',
      });
    } catch (error) {
      toast({
        title: 'Failed to restore',
        description:
          error.response?.data?.error || 'Could not restore the discussion.',
        variant: 'destructive',
      });
    }
  };

  // Format remaining time as MM:SS
  const formatRestoreTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle comment submission (with minting feedback)
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingComment(true);
    setSpinnerStatus('processing');
    setSpinnerError(null);

    const trimmedContent = commentContent.trim();
    if (!trimmedContent) return;

    try {
      await addDiscussionComment.mutateAsync({
        creator: walletAddress,
        content: trimmedContent,
        commentType: 'discussionComment',
      });
      setSpinnerStatus('success');
      setCommentContent(''); // Clear input on success
    } catch (err) {
      setSpinnerStatus('error');
      setSpinnerError(err?.message || 'Failed to mint comment NFT.');
    }
    // Do not setIsSubmittingComment(false) here; let the spinner control it
  };

  // Upvote/downvote mutations (same as home page)
  const upvoteMutation = upvoteInteraction();
  const downvoteMutation = downvoteInteraction();

  // Handle upvote
  const handleUpvote = async (discussionId) => {
    if (!user) return;
    try {
      await upvoteMutation.mutateAsync({
        discussionId,
        uOID: user?.oid,
        creator: walletAddress,
      });
    } catch (error) {
      // Show user-friendly error message
      toast.error('Failed to upvote', {
        description:
          error?.response?.data?.error ||
          'Something went wrong. Please try again.',
      });
    }
  };

  // Handle downvote
  const handleDownvote = async (discussionId) => {
    if (!user) return;
    try {
      await downvoteMutation.mutateAsync({
        discussionId,
        uOID: user.oid,
        creator: walletAddress,
      });
    } catch (error) {
      // Show user-friendly error message
      toast.error('Failed to downvote', {
        description:
          error?.response?.data?.error ||
          'Something went wrong. Please try again.',
      });
    }
  };

  // Get user initials for avatar fallback (for creator)
  const getCreatorInitials = React.useCallback(() => {
    if (!creatorProfile || !creatorProfile.username) return 'U';
    return creatorProfile.username
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  }, [creatorProfile]);

  // Handle edit mode
  const handleStartEdit = () => {
    setEditedTitle(discussion?.title || '');
    setEditedContent(discussion?.htmlContent || '');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedTitle('');
    setEditedContent('');
  };

  const handleSaveEdit = async () => {
    if (!editedTitle.trim() || !editedContent.trim()) {
      toast.error('Title and content are required');
      return;
    }

    setIsSavingEdit(true);
    try {
      const response = await axiosInstance.post(
        `/omniverse/discussion/${discussion.oid}/create-version`,
        {
          title: editedTitle,
          htmlContent: editedContent,
          editReason: editReason.trim() || undefined, // Optional edit reason
        }
      );

      if (response.data.success) {
        toast.success('Discussion updated successfully! Creating new version on blockchain...');
        // Refresh the discussion data to show new version
        window.location.reload();
      } else {
        toast.error('Failed to update discussion');
      }
    } catch (error) {
      console.error('Error updating discussion:', error);
      toast.error(error.response?.data?.error || 'Error updating discussion');
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Handle category selection
  const handleCategorySelect = (categorySlug) => {
    setSelectedCategory(categorySlug);
  };

  // Toggle categories visibility
  const toggleCategories = () => {
    setCategoriesCollapsed(!categoriesCollapsed);
  };

  // Toggle mobile categories dialog
  const toggleMobileCategories = () => {
    setMobileCategoriesOpen(!mobileCategoriesOpen);
  };

  const currentUser = user;

  // Block rendering if user is authenticated but user registration status is unknown
  // This prevents the flash of discussion content before redirect
  if (isAuthenticated && !isUserFetched) {
    return (
      <div className="min-h-screen bg-black text-zinc-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-zinc-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Block rendering if user is authenticated but doesn't exist or is not verified (will redirect via useEffect)
  if (isAuthenticated && isUserFetched && (!user || !user.isVerified)) {
    return (
      <div className="min-h-screen bg-black text-zinc-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-zinc-400">Redirecting to registration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-zinc-100 font-sans selection:bg-purple-500/30 flex flex-col">
      {/* Unique View Celebration */}
      <UniqueViewCelebration
        show={showUniqueViewCelebration}
        onComplete={() => setShowUniqueViewCelebration(false)}
      />

      {/* Gradient Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
      </div>
      <div className="fixed inset-0 z-0 pointer-events-none bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />

      {/* Mobile Categories Dialog */}
      <MobileCategoriesDialog
        categories={categories}
        selectedCategory={selectedCategory}
        onSelectCategory={handleCategorySelect}
        isOpen={mobileCategoriesOpen}
        onClose={() => setMobileCategoriesOpen(false)}
      />

      {/* Navbar - Full Width at Top */}
      <div className="sticky top-0 z-40 w-full bg-black/60 backdrop-blur-xl border-b border-white/5">
        <Navbar />
      </div>

      <div className="flex flex-1 relative z-10">
        {/* Left sidebar - Hidden on mobile */}
        <aside
          className={`hidden md:flex flex-col sticky top-[73px] h-[calc(100vh-73px)] bg-black/40 backdrop-blur-xl border-r border-white/5 overflow-y-auto custom-scrollbar transition-all duration-300 ${
            isSidebarCollapsed ? 'w-20' : 'w-72'
          }`}
        >
          <div className="p-4 flex flex-col h-full">
            {/* Collapse Toggle Button */}
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="absolute -right-3 top-6 z-50 bg-black border border-violet-500/30 text-violet-400 rounded-full p-1 shadow-[0_0_10px_rgba(139,92,246,0.2)] hover:shadow-[0_0_15px_rgba(139,92,246,0.4)] hover:text-white hover:border-violet-400 transition-all duration-300"
            >
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-300 ${
                  isSidebarCollapsed ? '-rotate-90' : 'rotate-90'
                }`}
              />
            </button>

            <div className="space-y-6 mt-4">
              <div>
                {!isSidebarCollapsed && (
                  <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3 px-2">
                    Menu
                  </h3>
                )}
                <nav className="space-y-1">
                  <Link
                    to="/forum"
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5 text-white font-medium ${
                      isSidebarCollapsed ? 'justify-center' : ''
                    }`}
                    title={isSidebarCollapsed ? 'Discussions' : ''}
                  >
                    <LayoutGrid className="h-5 w-5 text-violet-400 flex-shrink-0" />
                    {!isSidebarCollapsed && <span>Discussions</span>}
                  </Link>
                  <Link
                    to="/profile"
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-colors ${
                      isSidebarCollapsed ? 'justify-center' : ''
                    }`}
                    title={isSidebarCollapsed ? 'My Profile' : ''}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="flex-shrink-0"
                    >
                      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    {!isSidebarCollapsed && <span>My Profile</span>}
                  </Link>
                </nav>
              </div>

              <div>
                <div
                  className={`flex items-center ${
                    isSidebarCollapsed ? 'justify-center' : 'justify-between'
                  } mb-3 px-2 cursor-pointer group`}
                  onClick={toggleCategories}
                  title={isSidebarCollapsed ? 'Categories' : ''}
                >
                  {!isSidebarCollapsed && (
                    <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider group-hover:text-zinc-300 transition-colors">
                      Categories
                    </h3>
                  )}
                  <ChevronDown
                    className={`w-3 h-3 text-zinc-500 transition-transform ${
                      categoriesCollapsed ? '-rotate-90' : ''
                    } ${isSidebarCollapsed ? 'hidden' : ''}`}
                  />
                  {isSidebarCollapsed && (
                    <ListIcon className="h-4 w-4 text-zinc-500" />
                  )}
                </div>

                {!categoriesCollapsed && (
                  <div className="space-y-1">
                    <Link
                      to="/forum"
                      className={`w-full text-left py-2 px-3 rounded-lg flex items-center gap-3 transition-all duration-200 ${
                        selectedCategory === ''
                          ? 'bg-violet-500/10 text-violet-200 border border-violet-500/20'
                          : 'text-zinc-400 hover:text-white hover:bg-white/5'
                      } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                      title={isSidebarCollapsed ? 'All Discussions' : ''}
                    >
                      <div
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          selectedCategory === ''
                            ? 'bg-violet-400'
                            : 'bg-zinc-600'
                        }`}
                      />
                      {!isSidebarCollapsed && (
                        <span className="text-sm font-medium">
                          All Discussions
                        </span>
                      )}
                    </Link>

                    {categories.map((category) => (
                      <Link
                        key={category.slug}
                        to={`/forum?category=${category.slug}`}
                        className={`w-full text-left py-2 px-3 rounded-lg flex items-center gap-3 transition-all duration-200 ${
                          selectedCategory === category.slug
                            ? 'bg-white/10 text-white border border-white/10'
                            : 'text-zinc-400 hover:text-white hover:bg-white/5'
                        } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                        title={isSidebarCollapsed ? category.name : ''}
                      >
                        <div
                          className="w-2 h-2 rounded-full shadow-[0_0_8px_rgba(0,0,0,0.5)] flex-shrink-0"
                          style={{
                            backgroundColor: category.color || '#3B82F6',
                            boxShadow:
                              selectedCategory === category.slug
                                ? `0 0 8px ${category.color}`
                                : 'none',
                          }}
                        />
                        {!isSidebarCollapsed && (
                          <span className="text-sm font-medium truncate">
                            {category.name}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div
              className={`mt-auto pt-6 border-t border-white/5 ${
                isSidebarCollapsed ? 'hidden' : ''
              }`}
            >
              <div className="flex items-center gap-3 text-xs text-zinc-500">
                <span>© 2024 Talktrail</span>
                <span>•</span>
                <Link to="/privacy" className="hover:text-zinc-300">
                  Privacy
                </Link>
              </div>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-4 md:p-8 max-w-5xl mx-auto w-full min-w-0 transition-all duration-300">
          <ForumBackground />

          {/* Comment Minting Loader replaced with ProcessingSpinner */}
          <ProcessingSpinner
            isOpen={isSubmittingComment}
            status={spinnerStatus}
            error={spinnerError}
            onClose={() => {
              setIsSubmittingComment(false);
              setSpinnerStatus('processing');
              setSpinnerError(null);
            }}
            onRetry={() => {
              setSpinnerStatus('processing');
              setSpinnerError(null);
              handleCommentSubmit({ preventDefault: () => {} });
            }}
            title="Posting Comment"
            processingMessage="Your comment is being posted. Please wait..."
            successMessage="Your comment has been posted!"
            errorMessage="There was an error posting your comment."
          />

          {/* Mobile category selector */}
          <div className="md:hidden mb-4">
            <Button
              variant="outline"
              onClick={toggleMobileCategories}
              className="w-full justify-between border-white/10 text-white hover:bg-white/5"
            >
              <span>Categories</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>

          <button
            onClick={() => {
              // Check URL query params first, then location state
              const params = new URLSearchParams(location.search);
              const returnToParam = params.get('returnTo');
              const tabParam = params.get('tab');
              const activityIdParam = params.get('activityId');
              const pageParam = params.get('page');
              const returnTo = location.state?.returnTo;

              if (returnToParam) {
                // Build return URL with query params for highlighting
                let returnUrl = `${returnToParam}?from=${discussionOid}`;
                if (pageParam) returnUrl += `&page=${pageParam}`;
                if (tabParam) returnUrl += `&tab=${tabParam}`;
                if (activityIdParam)
                  returnUrl += `&activityId=${activityIdParam}`;
                navigate(returnUrl);
              } else if (returnTo) {
                navigate(returnTo);
              } else {
                navigate('/forum');
              }
            }}
            className="inline-flex items-center text-sm text-zinc-400 hover:text-white mb-6 transition-colors cursor-pointer"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {location.state?.returnTo ||
            new URLSearchParams(location.search).get('returnTo')
              ? 'Back to Profile'
              : 'Back to Forum'}
          </button>

          <div>
            {isLoadingDiscussion ? (
              <div className="space-y-4 animate-pulse">
                <div className="h-8 bg-zinc-800 rounded w-3/4"></div>
                <div className="h-4 bg-zinc-800 rounded w-1/4"></div>
                <div className="h-32 bg-zinc-800 rounded w-full mt-4"></div>
              </div>
            ) : discussion ? (
              <div className="bg-zinc-900/50 rounded-lg shadow-sm border border-white/10 overflow-hidden">
                {/* Cover Image - Only shown when discussion has a banner */}
                {discussion.banner && (
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={discussion.banner}
                      alt={discussion.title || 'Cover'}
                      className="w-full h-full object-cover opacity-80"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/30 to-transparent" />
                  </div>
                )}

                {/* Main Discussion */}
                <div className="p-6">
                  <DiscussionHeader
                    creatorProfile={creatorProfile}
                    isLoadingCreator={isLoadingCreator}
                    discussion={discussion}
                    getCreatorInitials={getCreatorInitials}
                    currentUser={currentUser}
                    isEditing={isEditing}
                    setDeleteTarget={setDeleteTarget}
                    setDeleteDialogOpen={setDeleteDialogOpen}
                    onRestore={handleRestoreDiscussion}
                  />{' '}
                  {isEditing ? (
                    <>
                      {/* Edit Mode */}
                      <div className="mb-4">
                        <Label
                          htmlFor="edit-title"
                          className="text-white mb-2 block"
                        >
                          Title
                        </Label>
                        <Input
                          id="edit-title"
                          value={editedTitle}
                          onChange={(e) => setEditedTitle(e.target.value)}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="Discussion title"
                        />
                      </div>

                      <div className="mb-4">
                        <Label
                          htmlFor="edit-reason"
                          className="text-white mb-2 block"
                        >
                          Edit Reason <span className="text-zinc-500">(optional)</span>
                        </Label>
                        <Textarea
                          id="edit-reason"
                          value={editReason}
                          onChange={(e) => setEditReason(e.target.value)}
                          className="bg-zinc-800 border-zinc-700 text-white resize-none"
                          placeholder="Briefly describe what you changed (optional)..."
                          rows={2}
                        />
                        <p className="text-xs text-zinc-500 mt-1">
                          This edit will create a new version on the blockchain.
                        </p>
                      </div>

                      <div className="mb-4">
                        <Label
                          htmlFor="edit-content"
                          className="text-white mb-2 block"
                        >
                          Content
                        </Label>
                        <Editor
                          tinymceScriptSrc={`${TINYMCE_CDN_URL}/tinymce.min.js`}
                          onInit={(evt, editor) =>
                            (editEditorRef.current = editor)
                          }
                          value={editedContent}
                          onEditorChange={(content) =>
                            setEditedContent(content)
                          }
                          init={{
                            license_key: 'gpl', // Required for TinyMCE 7+ open source usage
                            menubar: false,
                            // CDN-hosted TinyMCE paths (media.tracverse.com)
                            skin_url: `${TINYMCE_CDN_URL}/skins/ui/oxide-dark`,
                            content_css: `${TINYMCE_CDN_URL}/skins/content/dark/content.min.css`,
                            height: 500,
                            plugins:
                              'autolink charmap codesample emoticons image link lists media searchreplace table visualblocks wordcount code',
                            toolbar:
                              'bold italic underline | link image media | bullist numlist | code',
                            branding: false,
                            promotion: false,
                            image_caption: true,
                            content_style: `
                              body { background-color: #18181b; color: #fff; font-family: Inter, sans-serif; padding: 16px; }
                              img { max-width: 100%; height: auto; margin: 10px 0; border-radius: 8px; }
                            `,
                            file_picker_types: 'image',
                            automatic_uploads: true,
                            images_upload_handler: async (blobInfo, progress) => {
                              try {
                                const file = blobInfo.blob();
                                if (file.size > 2 * 1024 * 1024) {
                                  throw new Error('Image size should not exceed 2MB');
                                }
                                const formData = new FormData();
                                formData.append('file', file, blobInfo.filename());
                                const response = await axiosInstance.post(
                                  `/omniverse/upload/discussion-image`,
                                  formData,
                                  {
                                    headers: {
                                      'Content-Type': 'multipart/form-data',
                                    },
                                    onUploadProgress: (progressEvent) => {
                                      progress((progressEvent.loaded / progressEvent.total) * 100);
                                    },
                                  }
                                );
                                if (response.data.success && response.data.url) {
                                  return response.data.url;
                                } else {
                                  throw new Error('Upload failed');
                                }
                              } catch (error) {
                                if (editEditorRef.current) {
                                  editEditorRef.current.notificationManager.open({
                                    text: error.message || 'Image upload failed',
                                    type: 'error',
                                    timeout: 3000,
                                  });
                                }
                                throw error;
                              }
                            },
                            file_picker_callback: (cb, value, meta) => {
                              const input = document.createElement('input');
                              input.setAttribute('type', 'file');
                              input.setAttribute('accept', 'image/*');
                              input.addEventListener('change', (e) => {
                                const file = e.target.files[0];
                                if (file.size > 2 * 1024 * 1024) {
                                  if (editEditorRef.current) {
                                    editEditorRef.current.notificationManager.open({
                                      text: 'Image size should not exceed 2MB',
                                      type: 'error',
                                      timeout: 3000,
                                    });
                                  }
                                  return;
                                }
                                const reader = new FileReader();
                                reader.addEventListener('load', () => {
                                  const id = 'blobid' + new Date().getTime();
                                  const blobCache = window.tinymce.activeEditor.editorUpload.blobCache;
                                  const base64 = reader.result.split(',')[1];
                                  const blobInfo = blobCache.create(id, file, base64);
                                  blobCache.add(blobInfo);
                                  cb(blobInfo.blobUri(), { title: file.name });
                                });
                                reader.readAsDataURL(file);
                              });
                              input.click();
                            },
                          }}
                        />
                      </div>

                      <div className="flex gap-3 mb-6">
                        <Button
                          onClick={handleSaveEdit}
                          disabled={isSavingEdit}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          {isSavingEdit ? 'Saving...' : 'Save Changes'}
                        </Button>
                        <Button
                          onClick={handleCancelEdit}
                          disabled={isSavingEdit}
                          variant="outline"
                          className="border-zinc-700 text-white hover:bg-zinc-800"
                        >
                          Cancel
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* View Mode */}
                      <h1 className="text-2xl font-bold mb-2 text-white">
                        {discussion?.title}
                      </h1>

                      <div
                        className="prose prose-invert max-w-none mb-6 text-zinc-300"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            discussion?.htmlContent || ''
                          ),
                        }}
                      />
                    </>
                  )}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 pt-4 border-t border-white/10">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUpvote(discussion?.oid)}
                        className="flex items-center gap-1 text-zinc-400 hover:text-white hover:bg-white/5 text-xs sm:text-sm"
                      >
                        <ThumbsUp className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                        <span className="whitespace-nowrap">
                          {discussion?.upvotes ?? 0}
                        </span>
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownvote(discussion?.oid)}
                        className="flex items-center gap-1 text-zinc-400 hover:text-white hover:bg-white/5 text-xs sm:text-sm"
                      >
                        <ThumbsDown className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                        <span className="whitespace-nowrap">
                          {discussion?.downvotes ?? 0}
                        </span>
                      </Button>

                      <div className="flex items-center gap-1 text-xs sm:text-sm text-zinc-500">
                        <MessageSquare className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                        <span className="whitespace-nowrap">
                          {comments?.length} comments
                        </span>
                      </div>

                      {/* View status - show unique view and power views earned */}
                      {walletAddress && viewStatus && (
                        <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                          <div
                            className={`flex items-center gap-1 ${
                              viewStatus.hasUniqueView
                                ? 'text-emerald-400'
                                : 'text-zinc-500'
                            }`}
                            title={
                              viewStatus.hasUniqueView
                                ? 'Unique view earned (+10 View Power)'
                                : 'No unique view yet'
                            }
                          >
                            <Eye className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                            <span className="whitespace-nowrap">
                              {viewStatus.hasUniqueView
                                ? '✓ Unique'
                                : 'No unique'}
                            </span>
                          </div>
                          <div
                            className={`flex items-center gap-1 ${
                              viewStatus.powerViewCount > 0
                                ? 'text-purple-400'
                                : 'text-zinc-500'
                            }`}
                            title={`${
                              viewStatus.powerViewCount
                            } power views (+${
                              viewStatus.powerViewCount * 2
                            } View Power)`}
                          >
                            <Zap className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                            <span className="whitespace-nowrap">
                              {viewStatus.powerViewCount} power
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Report button - for anyone who is not the creator */}
                    <div className="flex items-center gap-2">
                      {walletAddress &&
                        walletAddress?.toLowerCase() !==
                          discussion?.creator?.toLowerCase() && (
                          <ReportDialog
                            entityType="discussion"
                            entityId={discussion?._id}
                            entityOID={discussion?.oid}
                            contentPreview={discussion?.title}
                            trigger={
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex items-center gap-1 text-zinc-400 hover:text-red-400 hover:bg-white/5"
                              >
                                <Flag className="h-4 w-4" />
                                <span className="hidden sm:inline">Report</span>
                              </Button>
                            }
                          />
                        )}
                    </div>
                  </div>
                </div>

                {/* Frozen Discussion Alert */}
                {discussion?.frozen && (
                  <div className="border-t border-blue-900/30 p-6 bg-blue-900/10">
                    <Alert className="border-blue-900/50 bg-blue-900/20">
                      <Snowflake className="h-4 w-4 text-blue-400" />
                      <AlertDescription className="text-blue-200 font-medium">
                        This discussion has been frozen by moderators. No new
                        comments can be added.
                      </AlertDescription>
                    </Alert>
                  </div>
                )}

                {/* Version History Tabs */}
                {discussion?.currentVersion > 1 && (
                  <div className="px-6">
                    <DiscussionVersionTabs
                      discussionOid={discussion.oid}
                      currentVersion={discussion.currentVersion}
                      onVersionRestore={() => {
                        // Refresh discussion after version restore
                        window.location.reload();
                      }}
                    />
                  </div>
                )}

                {/* Comments Section - Connected Layout with Position Tracker */}
                {comments && comments.length > 0 && (
                  <div className="flex items-start">
                    {/* Comments list */}
                    <div className="flex-1 min-w-0">
                      {comments
                        .filter((comment) => !comment.parentId)
                        .map((comment, index) => (
                          <div key={comment?.oid}>
                            <div className="border-t-8 border-black"></div>
                            <ForumCommentItem
                              comment={comment}
                              postId={discussion?.oid}
                              currentUser={currentUser}
                            />
                          </div>
                        ))}
                    </div>
                    {/* Comment Position Tracker - Sticky sidebar on desktop */}
                    <CommentPositionTracker
                      comments={comments}
                      discussionDate={discussion?.createdAt}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 border border-white/10 rounded-lg bg-zinc-900/50">
                <h3 className="text-xl font-bold mb-2 text-white">
                  Discussion not found
                </h3>
                <p className="text-zinc-400 mb-4">
                  The discussion you're looking for doesn't exist or has been
                  removed.
                </p>
                <Link to="/forum">
                  <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                    Back to Forum
                  </Button>
                </Link>
              </div>
            )}
            {/* Comment Form - Only show if discussion is not frozen and user can post */}
            {!discussion?.frozen && (!discussion?.communityOid || canPost) && (
              <div className="mt-8">
                <h2 className="text-xl font-bold mb-4 text-white">
                  Add Comment
                </h2>
                <div className="flex gap-4">
                  <Avatar className="h-10 w-10 border border-zinc-700">
                    <AvatarImage
                      src={currentUser?.avatar || '/placeholder.svg'}
                      alt={currentUser?.username || 'User'}
                    />
                    <AvatarFallback className="bg-zinc-800 text-zinc-400">
                      {currentUser?.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <Textarea
                      placeholder="Write a comment..."
                      value={commentContent}
                      onChange={(e) => setCommentContent(e.target.value)}
                      maxLength={10000}
                      className="min-h-[100px] bg-zinc-900 border-white/10 text-white placeholder:text-zinc-500 focus:border-purple-500"
                    />
                    <div className="mt-2 flex items-center justify-between">
                      <span
                        className={`text-xs ${
                          commentContent.length > 10000
                            ? 'text-red-500'
                            : commentContent.length > 9000
                            ? 'text-orange-500'
                            : commentContent.length > 7500
                            ? 'text-yellow-500'
                            : 'text-zinc-500'
                        }`}
                      >
                        {commentContent.length.toLocaleString()} / 10,000
                      </span>
                      <Button
                        onClick={handleCommentSubmit}
                        disabled={
                          !commentContent.trim() ||
                          commentContent.length > 10000 ||
                          isSubmittingComment
                        }
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                      >
                        {isSubmittingComment ? 'Posting...' : 'Post Comment'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Non-member Message - Show when user is not a member of the community */}
            {!discussion?.frozen &&
              discussion?.communityOid &&
              !canPost &&
              user && (
                <div className="mt-8 text-center py-8 bg-zinc-900/50 rounded-lg border border-zinc-800">
                  <div className="flex flex-col items-center gap-3">
                    <div className="p-3 bg-zinc-800 rounded-full">
                      <MessageSquare className="h-6 w-6 text-zinc-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">
                      Join to Comment
                    </h3>
                    <p className="text-zinc-400 text-sm max-w-md">
                      You need to join this community to comment on discussions.
                    </p>
                  </div>
                </div>
              )}
            {/* Frozen Discussion Message - Show instead of comment form when frozen */}
            {discussion?.frozen && (
              <div className="mt-8 text-center py-8 bg-blue-900/10 rounded-lg border border-blue-900/30">
                <div className="flex flex-col items-center gap-3">
                  <div className="p-3 bg-blue-900/20 rounded-full">
                    <Snowflake className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-blue-200 mb-1">
                      Discussion Frozen
                    </h3>
                    <p className="text-blue-300/80">
                      This discussion has been frozen by moderators and is no
                      longer accepting new comments.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {/* Loading/Empty States for Comments */}
            {isCommentsLoading && (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                <p className="mt-2 text-zinc-400">Loading comments...</p>
              </div>
            )}
            {!isCommentsLoading && (!comments || comments.length === 0) && (
              <div className="text-center py-8 bg-zinc-900/30 rounded-lg mt-8 border border-white/5">
                <p className="text-zinc-400">
                  No comments yet. Be the first to start the discussion!
                </p>
              </div>
            )}
            {/* Spacer for mobile bottom bar */}
            <div className="h-20 md:hidden" />
          </div>
        </main>
      </div>

      <Footer />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-gray-900 border border-gray-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-white">
              Delete Discussion
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300 space-y-3">
              <p>Are you sure you want to delete this discussion?</p>
              <p className="text-emerald-400 font-medium">
                💡 You'll have 5 minutes to restore this discussion after
                deletion.
              </p>
              <p className="text-amber-400 font-medium">
                ⚠️ Important: Deleting removes this discussion from the
                Talktrail interface, but all knowledge assets published to the
                OriginTrail Decentralized Knowledge Graph are public and
                permanent. This content cannot be deleted from the blockchain.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 text-white hover:bg-gray-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleteTarget && deleteDiscussion) {
                  try {
                    const result = await deleteDiscussion().mutateAsync({
                      discussionOid: deleteTarget,
                    });
                    // Set restore data with deadline from server response
                    if (result.canRestore && result.restoreDeadline) {
                      setRestoreData({
                        discussionOid: deleteTarget,
                        deadline: result.restoreDeadline,
                      });
                      toast({
                        title: 'Discussion deleted',
                        description: 'You have 5 minutes to restore it.',
                      });
                    }
                  } catch (error) {
                    toast({
                      title: 'Failed to delete',
                      description:
                        error.response?.data?.error ||
                        'Could not delete the discussion.',
                      variant: 'destructive',
                    });
                  }
                  setDeleteTarget(null);
                }
              }}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Banner - shown when discussion is recently deleted */}
      {restoreData && restoreTimeLeft > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
          <div className="bg-zinc-900 border border-emerald-500/50 rounded-lg shadow-lg p-4 flex items-center gap-4">
            <div className="flex items-center gap-2 text-zinc-300">
              <RotateCcw className="w-5 h-5 text-emerald-400" />
              <span>Discussion deleted. Restore within</span>
              <span className="font-mono font-bold text-emerald-400">
                {formatRestoreTime(restoreTimeLeft)}
              </span>
            </div>
            <Button
              onClick={handleRestoreDiscussion}
              variant="outline"
              className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/10"
            >
              Restore
            </Button>
            <Button
              onClick={() => setRestoreData(null)}
              variant="ghost"
              size="sm"
              className="text-zinc-500 hover:text-zinc-300"
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
